import { UserChatStatus } from '@/components/chat/_domain/modules/user-chat-status-service/user-chat-status-service-interface';
import * as clientTypes from '@/components/chat/_domain/models/client-types.data.json';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { ConfigurationPaths, IConfigurationService } from '@/app-modules/config';
import { injectable, inject } from 'inversify';

interface IClientData {
  platform: string;
  os: string;
  browser?: string;
}

class RoomUserModel {

  public username: string;
  public chatRoom: string;
  public birthday: boolean;
  public chatPrize: boolean;
  public notEligibleForPromo: boolean;
  public notEligibleForChatPrize: boolean;
  public newMember: boolean;
  public returningPlayer: boolean;
  public client: string;
  public clientType: string;
  public readableClientType: IClientData;
  public twiceSinned: boolean;
  public chatTroubleMaker: boolean;
  public readonly sortableUsername: string;

  constructor(user: any, public userChatStatus: UserChatStatus, public gameName: string, public chatRoomName: string, public birthdaysEnabled: boolean) {
    this.username = user.username;
    this.chatRoom = chatRoomName;
    this.birthday = user.birthday;
    this.chatPrize = user.chatPrize;
    this.notEligibleForPromo = user.notEligibleForPromo;
    this.notEligibleForChatPrize = user.notEligibleForChatPrize;
    this.newMember = user.newMember;
    this.returningPlayer = user.returningPlayer;
    this.client = user.client;
    this.clientType = user.clientType;
    this.readableClientType = this.parseClientType();
    this.twiceSinned = user.twiceSinned;
    this.chatTroubleMaker = user.troubleMaker;
    this.sortableUsername = this.determineSortableUsername();
  }

  private determineDisplayedUsername(): string {
    if (this.userChatStatus === UserChatStatus.TeamLeader) {
      const cmPrefix = this.username.substr(0, 2);
      return this.username.replace(`${cmPrefix}-`, `${cmPrefix}-TL-`);
    }
    if (this.userChatStatus === UserChatStatus.ShiftLeader) {
      const cmPrefix = this.username.substr(0, 2);
      return this.username.replace(`${cmPrefix}-`, `${cmPrefix}-SL-`);
    }
    return this.username;
  }

  private determineSortableUsername(): string {
    let username = this.determineDisplayedUsername();
    if (this.birthday && this.birthdaysEnabled) {
      username = `${this.getFontAwesomeMarkup('fa-usd')}${username}`;
    }
    else if (this.newMember) {
      username = `${this.getFontAwesomeMarkup('fa-plus')}${username}`;
    }
    else if (this.returningPlayer) {
      username = `${this.getFontAwesomeMarkup('fa-hashtag')}${username}`;
    }
    if (this.chatPrize) {
      username = `${username}${this.getFontAwesomeMarkup('fa-asterisk')}`;
    }
    if (this.notEligibleForPromo){
      username = `${username}${this.getFontAwesomeMarkup('fa-chevron-up')}`;
    }
    if (this.notEligibleForChatPrize){
      username = `${username}${this.getFontAwesomeMarkup('fa-chevron-up')}`;
    }
    return username;
  }

  private getFontAwesomeMarkup(iconClass: string): string {
    return `<i class="fa ${iconClass}" aria-hidden="true"></i>`;
  }

  public getClientType(): string
  {
    if (!this.clientType) {
      return '';
    }
    if (this.clientType.length <= 2) {
      return this.clientType;
    }
    return this.clientType.substr(0, 2) + 'xxx';
  }

  public parseClientType(): IClientData {
    const data: IClientData = clientTypes.clients[this.getClientType()];
    return data || { platform: 'Unknown', os: 'Unknown' };
  }

  public getClientVersion(): string
  {
    if (!this.clientType || this.clientType.length <= 2) {
      return 'Unknown';
    }
    return this.clientType.substr(0, 2);
  }

  public getCssClassBasedOnUserInfo(): string {
    let mainClass = this.userChatStatus !== UserChatStatus.StandardUser ? 'cm-user' : this.getMainUsernameColour();
    if (this.chatPrize) mainClass += ' bling-user';
    if (this.notEligibleForPromo) mainClass += ' notEligibleForPromo-user';
    if (this.notEligibleForChatPrize) mainClass += ' notEligibleForChatPrize-user ';
    if (this.userChatStatus === UserChatStatus.TeamLeader) mainClass += ' tl-user';
    if (this.userChatStatus === UserChatStatus.ShiftLeader) mainClass += ' sl-user';

    return mainClass;
  }

  private getMainUsernameColour(): string {
    if (this.twiceSinned) return 'twice-sinned';
    if (this.chatTroubleMaker) return 'chat-troublemaker';
    if (this.birthday && this.birthdaysEnabled) return 'birthday';
    if (this.newMember) return 'new-member';
    if (this.returningPlayer) return 'returning-lapsed';
  }
}

export { RoomUserModel, IClientData };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/_domain/models/room-user-model.ts